export function widgetCss(
    primary: string,
    accent: string,
    background: string = "#f7f9fb",
    panel: string = "#ffffff",
    text: string = "#2a3439",
    muted: string = "#566166",
    border: string = "#a9b4b9",
    font: string = "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial",
    buttonTextColor: string = "#ffffff"
) {
    return `
:host{
  --bk-font: ${font};
  --bk-primary: ${primary || "#565e74"};
  --bk-accent: ${accent || "#9e0625"};
  --bk-button-text-color: ${buttonTextColor || "#ffffff"};

  --bk-bg: ${background || "#f7f9fb"};
  --bk-surface: ${background || "#f7f9fb"};
  --bk-panel: ${panel || "#ffffff"};
  --bk-surface-low: color-mix(in srgb, ${background || "#f7f9fb"} 88%, #dbe4ea 12%);
  --bk-surface-high: color-mix(in srgb, ${background || "#f7f9fb"} 78%, #d0dbe3 22%);
  --bk-surface-highest: color-mix(in srgb, ${background || "#f7f9fb"} 65%, #c6d2db 35%);

  --bk-text: ${text || "#2a3439"};
  --bk-muted: ${muted || "#566166"};
  --bk-outline: color-mix(in srgb, ${border || "#a9b4b9"} 72%, #717c82 28%);
  --bk-outline-variant: ${border || "#a9b4b9"};

  --bk-primary-dim: color-mix(in srgb, ${primary || "#565e74"} 82%, black 18%);
  --bk-primary-container: color-mix(in srgb, ${primary || "#565e74"} 18%, white 82%);
  --bk-secondary-container: color-mix(in srgb, ${accent || "#9e0625"} 14%, white 86%);
  --bk-on-secondary-container: color-mix(in srgb, ${accent || "#9e0625"} 58%, black 42%);

  --bk-danger-bg: #fee2e2;
  --bk-danger-text: #991b1b;
  --bk-success-bg: #dcfce7;
  --bk-success-text: #166534;

  --bk-shadow-lg: 0 20px 40px rgba(42,52,57,.14);
  --bk-shadow-md: 0 4px 16px rgba(42,52,57,.04);

  --bk-radius: 16px;
  --bk-radius-sm: 12px;
  --bk-radius-xs: 10px;

  --bk-width: 520px;

  --bk-offset-x: 24px;
  --bk-offset-y: 24px;

  --bk-modal-top: auto;
  --bk-modal-right: 24px;
  --bk-modal-bottom: 24px;
  --bk-modal-left: auto;

  --bk-fab-top: auto;
  --bk-fab-right: 24px;
  --bk-fab-bottom: 24px;
  --bk-fab-left: auto;

  --bk-fab-radius: 999px;
  --bk-fab-width: auto;
  --bk-fab-height: auto;
  --bk-fab-padding: 12px 16px;
}

*{ box-sizing:border-box; }

:host, .bk-root{
  font-family: var(--bk-font);
  color: var(--bk-text);
}

button, input, textarea, select{
  font: inherit;
}

.bk-root{
  min-width: 0;
}

/* inline */
.bk-inline{
  background: var(--bk-surface);
  border-radius: var(--bk-radius);
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  height: 680px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--bk-shadow-md);
}

/* fab */
.bk-fab{
  position: fixed;
  top: var(--bk-fab-top);
  right: var(--bk-fab-right);
  bottom: var(--bk-fab-bottom);
  left: var(--bk-fab-left);
  z-index: 2147483647;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:10px;

  width: var(--bk-fab-width);
  height: var(--bk-fab-height);
  padding: var(--bk-fab-padding);

  border-radius: var(--bk-fab-radius);
  border: 0;
  background: var(--bk-primary);
  color: var(--bk-button-text-color);
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: .2px;
  box-shadow: var(--bk-shadow-lg);
  transition: transform .2s ease, filter .2s ease, opacity .16s ease;
}
.bk-fab:hover{ filter: brightness(.98); transform: translateY(-1px); }
.bk-fab:active{ transform: translateY(0px); }
.bk-fab-hidden{
  opacity: 0;
  transform: scale(.9);
  pointer-events: none;
}

/* open/close animaties — timings staan ook in widget.tsx (CLOSE_ANIMATION_MS) */
@keyframes bk-overlay-in{
  from{ opacity: 0; }
  to{ opacity: 1; }
}
@keyframes bk-overlay-out{
  from{ opacity: 1; }
  to{ opacity: 0; }
}
@keyframes bk-modal-in{
  from{ opacity: 0; transform: translate(-50%, -50%) scale(.94) translateY(10px); }
  to{ opacity: 1; transform: translate(-50%, -50%) scale(1) translateY(0); }
}
@keyframes bk-modal-out{
  from{ opacity: 1; transform: translate(-50%, -50%) scale(1) translateY(0); }
  to{ opacity: 0; transform: translate(-50%, -50%) scale(.96) translateY(6px); }
}

/* overlay */
.bk-overlay{
  position: fixed;
  inset: 0;
  background: rgba(17,24,39,.50);
  z-index: 2147483646;
  backdrop-filter: blur(6px);
  animation: bk-overlay-in .22s ease both;
}
.bk-overlay.bk-closing{
  animation: bk-overlay-out .16s ease both;
}

/* modal */
.bk-modal{
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2147483647;

  width: 75vw;
  max-width: 1100px;
  min-width: 320px;
  max-height: 85vh;

  background: color-mix(in srgb, var(--bk-surface) 92%, white 8%);
  backdrop-filter: blur(20px);
  border-radius: var(--bk-radius);
  box-shadow: var(--bk-shadow-lg);
  overflow: hidden;

  display: flex;
  flex-direction: column;

  animation: bk-modal-in .28s cubic-bezier(.22,1,.36,1) both;
  transform-origin: center;
}
.bk-modal.bk-closing{
  animation: bk-modal-out .18s cubic-bezier(.4,0,1,1) both;
}

@media (prefers-reduced-motion: reduce){
  .bk-overlay, .bk-overlay.bk-closing,
  .bk-modal, .bk-modal.bk-closing{
    animation: none;
  }
  .bk-fab-hidden{
    opacity: 0;
    transform: none;
  }
}

.bk-modal-header{
  position: sticky;
  top: 0;
  z-index: 20;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
  padding: 14px 16px;
  background: color-mix(in srgb, var(--bk-panel) 78%, white 22%);
  backdrop-filter: blur(20px);
}

.bk-title{
  display:flex;
  flex-direction:column;
  gap:2px;
}

.bk-title strong{
  font-size: 15px;
  letter-spacing: -.02em;
  color: var(--bk-text);
}

.bk-subtitle{
  font-size: 12px;
  color: var(--bk-muted);
}

.bk-close{
  border: 0;
  background: var(--bk-panel);
  color: var(--bk-text);
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
  box-shadow: var(--bk-shadow-md);
}

.bk-modal-body{
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

/* flow generic */
.bk-flow{
  position: relative;
  background: var(--bk-surface);
  min-height: 100%;
}

.bk-flow-inline{
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.bk-flow-popup{
  min-height: 100%;
}

.bk-flow-inline .bk-step-topbar{
  padding: 16px 18px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.bk-flow-inline .bk-step-body{
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 18px 18px 24px;
}

.bk-flow-inline .bk-page-title{
  font-size: 1.25rem;
}

.bk-flow-inline .bk-page-copy{
  font-size: 13px;
}

.bk-flow-inline .bk-step-section{
  max-width: 100%;
}

.bk-flow-inline .bk-staff-grid{
  grid-template-columns: 1fr;
  gap: 12px;
}

.bk-flow-inline .bk-datetime-step{
  grid-template-columns: 1fr;
  gap: 16px;
}

.bk-flow-inline .bk-datetime-main{
  grid-template-columns: 1fr;
  gap: 14px;
}

.bk-flow-inline .bk-details-layout{
  grid-template-columns: 1fr;
  gap: 16px;
}

.bk-flow-inline .bk-service-item,
.bk-flow-inline .bk-staff-card,
.bk-flow-inline .bk-calendar-card,
.bk-flow-inline .bk-time-card,
.bk-flow-inline .bk-summary-card,
.bk-flow-inline .bk-success-card{
  padding: 16px;
  border-radius: 14px;
}

.bk-flow-inline .bk-info-panel,
.bk-flow-inline .bk-context-card{
  padding: 14px;
  border-radius: 14px;
}

.bk-flow-inline .bk-bottom-nav{
  position: sticky;
  bottom: 0;
  z-index: 12;
  padding: 12px 18px;
}

.bk-step-topbar{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap: 16px;
  padding: 18px 24px;
  background: color-mix(in srgb, var(--bk-surface) 82%, white 18%);
  backdrop-filter: blur(20px);
  position: sticky;
  top: 0;
  z-index: 10;
}

.bk-brand{
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -.02em;
}

.bk-step-label{
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -.02em;
}

/* Genummerde voortgangs-stepper (Variant A: "Compacte wizard") */
.bk-stepper{
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.bk-stepper-item{
  display: flex;
  align-items: center;
  flex: 1 1 auto;
}

.bk-stepper-item:last-child{
  flex: 0 0 auto;
}

.bk-stepper-dot{
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 800;
  background: var(--bk-surface-low);
  color: var(--bk-muted);
  border: 2px solid var(--bk-outline-variant);
  transition: background .18s ease, color .18s ease, border-color .18s ease;
}

.bk-stepper-dot.is-active{
  background: var(--bk-primary);
  border-color: var(--bk-primary);
  color: var(--bk-button-text-color);
}

.bk-stepper-dot.is-done{
  background: var(--bk-primary-container);
  border-color: var(--bk-primary);
  color: var(--bk-primary-dim);
}

.bk-stepper-line{
  flex: 1 1 auto;
  height: 2px;
  min-width: 8px;
  margin: 0 4px;
  background: var(--bk-outline-variant);
  transition: background .18s ease;
}

.bk-stepper-line.is-done{
  background: var(--bk-primary);
}

/* taalwissel NL/EN */
.bk-lang-toggle{
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background: var(--bk-surface-low);
  flex-shrink: 0;
}

.bk-lang-btn{
  border: 0;
  background: transparent;
  color: var(--bk-muted);
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
  cursor: pointer;
  transition: background .16s ease, color .16s ease;
}
.bk-lang-btn-flag{
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  opacity: .55;
  transform: scale(.92);
}
.bk-lang-btn-flag svg{
  border-radius: 50%;
}
.bk-lang-btn-flag.is-active{
  opacity: 1;
  transform: scale(1);
  box-shadow: 0 0 0 2px var(--bk-panel), 0 0 0 3.5px var(--bk-primary);
}
.bk-lang-btn.is-active{
  background: var(--bk-panel);
  color: var(--bk-text);
  box-shadow: var(--bk-shadow-md);
}

.bk-step-body{
  padding: 24px 24px 104px;
}

.bk-step-section{
  max-width: 1040px;
  margin: 0 auto;
}

.bk-page-header{
  margin-bottom: 28px;
}

.bk-kicker{
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--bk-primary);
}

.bk-page-title{
  margin: 0 0 8px;
  font-size: 1.5rem;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--bk-text);
}

.bk-page-copy{
  margin: 0;
  font-size: .875rem;
  line-height: 1.6;
  color: var(--bk-muted);
}

/* alerts */
.bk-alert{
  margin: 16px 24px 0;
  padding: 12px 14px;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 14px;
}

.bk-flow-inline .bk-alert{
  margin: 12px 18px 0;
}

.bk-alert-error{
  background: var(--bk-danger-bg);
  color: var(--bk-danger-text);
}

.bk-alert-success{
  background: var(--bk-success-bg);
  color: var(--bk-success-text);
}

/* service list */
.bk-category-bar{
  display:flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 14px;
  scrollbar-width: none;
}
.bk-category-bar::-webkit-scrollbar{
  display:none;
}

.bk-category-pill{
  flex-shrink: 0;
  white-space: nowrap;
  border: 1.5px solid var(--bk-outline-variant);
  background: var(--bk-panel);
  color: var(--bk-text);
  border-radius: 999px;
  padding: 10px 18px;
  font-size: .8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.bk-category-pill.is-active{
  background: var(--bk-primary);
  border-color: var(--bk-primary);
  color: #fff;
}

.bk-service-search{
  position: relative;
  margin-bottom: 16px;
}
.bk-service-search-icon{
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--bk-muted);
  font-size: 16px;
  pointer-events: none;
}
.bk-service-search input{
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid var(--bk-outline-variant);
  background: var(--bk-surface-low);
  color: var(--bk-text);
  border-radius: 12px;
  padding: 12px 14px 12px 38px;
  font-size: .875rem;
  font-family: inherit;
  outline: none;
}
.bk-service-search input:focus{
  border-color: var(--bk-primary);
}

.bk-service-list{
  display:grid;
  gap: 12px;
}

.bk-service-item{
  width: 100%;
  text-align: left;
  border: 0;
  background: var(--bk-surface-low);
  border-radius: 16px;
  padding: 18px;
  cursor: pointer;
  transition: background .2s ease, transform .2s ease, box-shadow .2s ease;
}
.bk-service-item:hover{
  background: var(--bk-surface-high);
}
.bk-service-item.is-selected{
  background: var(--bk-panel);
  box-shadow: var(--bk-shadow-md);
}

.bk-service-top{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap: 12px;
}

.bk-service-title-wrap{
  display:flex;
  flex-wrap:wrap;
  gap: 10px;
  align-items:center;
}

.bk-service-title{
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -.02em;
}

.bk-service-arrow{
  font-size: 22px;
  color: var(--bk-muted);
}

.bk-service-meta{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap: 12px;
  margin-top: 12px;
  font-size: .875rem;
  color: var(--bk-muted);
}
.bk-service-meta strong{
  color: var(--bk-text);
}

.bk-chip{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background: var(--bk-secondary-container);
  color: var(--bk-on-secondary-container);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}
.bk-chip-muted{
  background: var(--bk-surface-low);
  color: var(--bk-muted);
}

/* info */
.bk-info-panel{
  margin-top: 28px;
  display:flex;
  gap: 12px;
  align-items:flex-start;
  background: var(--bk-surface-low);
  border-radius: 14px;
  padding: 16px;
  color: var(--bk-muted);
  font-size: 13px;
  line-height: 1.6;
}

.bk-info-icon{
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display:grid;
  place-items:center;
  background: var(--bk-primary-container);
  color: var(--bk-primary);
  font-weight: 800;
}

/* staff */
.bk-staff-grid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.bk-staff-card{
  text-align:left;
  border: 0;
  background: var(--bk-panel);
  border-radius: 16px;
  padding: 18px;
  cursor:pointer;
  transition: transform .2s ease, box-shadow .2s ease;
}
.bk-staff-card:hover{
  box-shadow: var(--bk-shadow-md);
}
.bk-staff-card.is-selected{
  box-shadow: 0 4px 24px rgba(42,52,57,.08);
  outline: 2px solid color-mix(in srgb, var(--bk-primary) 22%, transparent 78%);
}

.bk-staff-card-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap: 10px;
  margin-bottom: 16px;
}

.bk-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height: 24px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--bk-surface-high);
  color: var(--bk-muted);
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .1em;
}

.bk-badge.is-active{
  background: var(--bk-primary);
  color: #fff;
}

.bk-avatar{
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display:grid;
  place-items:center;
  background: var(--bk-surface-highest);
  color: var(--bk-primary-dim);
  font-weight: 700;
  font-size: 13px;
}

.bk-staff-content h3{
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -.02em;
}

.bk-staff-content p{
  margin: 0;
  color: var(--bk-muted);
  font-size: .875rem;
  line-height: 1.5;
}

.bk-staff-link{
  margin-top: 24px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
  color: var(--bk-primary);
  text-transform: uppercase;
}

/* datetime */
.bk-datetime-step{
  display:grid;
  grid-template-columns: 280px 1fr;
  gap: 20px;
}

.bk-datetime-sidebar{
  align-self:start;
}

.bk-context-card{
  margin-top: 20px;
  background: var(--bk-surface-low);
  border-radius: 16px;
  padding: 18px;
  display:grid;
  gap: 14px;
}

.bk-context-row{
  display:flex;
  align-items:center;
  gap: 10px;
  color: var(--bk-text);
  font-size: .925rem;
}

.bk-datetime-main{
  display:grid;
  grid-template-columns: 1.2fr .95fr;
  gap: 16px;
}

.bk-calendar-card,
.bk-time-card{
  background: var(--bk-panel);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--bk-shadow-md);
}

.bk-calendar-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 8px;
  margin-bottom: 18px;
}

.bk-calendar-head h2,
.bk-time-card h2{
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -.02em;
  text-transform: capitalize;
}

.bk-time-card h2{
  margin-bottom: 18px;
}

.bk-calendar-nav{
  border: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--bk-surface-low);
  color: var(--bk-text);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .2s ease, opacity .2s ease;
}
.bk-calendar-nav:hover:not(:disabled){
  background: color-mix(in srgb, var(--bk-primary) 14%, var(--bk-surface-low) 86%);
}
.bk-calendar-nav:disabled{
  opacity: .35;
  cursor: not-allowed;
}

.bk-calendar-weekdays{
  display:grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 10px;
}
.bk-calendar-weekdays span{
  text-align:center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--bk-outline);
}

.bk-calendar-grid{
  display:grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.bk-calendar-day{
  border: 0;
  height: 40px;
  border-radius: 12px;
  background: transparent;
  color: var(--bk-text);
  cursor: pointer;
  transition: background .2s ease, color .2s ease;
}
.bk-calendar-day:hover{
  background: var(--bk-surface-low);
}
.bk-calendar-day.is-outside{
  color: color-mix(in srgb, var(--bk-outline) 45%, transparent 55%);
}
.bk-calendar-day.is-selected{
  background: var(--bk-primary);
  color: #fff;
}
.bk-calendar-day.is-disabled{
  opacity: .4;
  cursor: not-allowed;
}

.bk-time-list{
  display:grid;
  gap: 10px;
  max-height: 320px;
  overflow:auto;
  padding-right: 2px;
}

.bk-flow-inline .bk-time-list{
  max-height: 260px;
}

.bk-time-item{
  border: 0;
  width: 100%;
  text-align:left;
  display:flex;
  justify-content:space-between;
  align-items:center;
  padding: 15px 16px;
  border-radius: 12px;
  background: var(--bk-surface-low);
  cursor:pointer;
  color: var(--bk-text);
  font-weight: 700;
}
.bk-time-item.is-selected{
  background: var(--bk-primary);
  color: #fff;
}
.bk-time-item-spots{
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  opacity: 0.7;
}

/* details */
.bk-details-layout{
  display:grid;
  grid-template-columns: 1.2fr .8fr;
  gap: 20px;
}

.bk-form-grid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.bk-field{
  display:grid;
  gap: 8px;
}

.bk-field span{
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: var(--bk-muted);
}

.bk-field-full{
  grid-column: 1 / -1;
}

.bk-checkbox-field{
  display:flex;
  align-items:flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--bk-surface-low);
  cursor:pointer;
}
.bk-checkbox-field input{
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  accent-color: var(--bk-primary);
  cursor:pointer;
}
.bk-checkbox-field-text{
  display:grid;
  gap: 3px;
}
.bk-checkbox-field-label{
  font-size: 13px;
  font-weight: 700;
  color: var(--bk-text);
}
.bk-checkbox-field-hint{
  font-size: 12px;
  line-height: 1.45;
  color: var(--bk-muted);
}

.bk-input,
.bk-select,
.bk-textarea{
  width: 100%;
  border: 0;
  border-bottom: 2px solid color-mix(in srgb, var(--bk-outline) 35%, transparent 65%);
  border-radius: 12px 12px 0 0;
  background: var(--bk-surface-high);
  color: var(--bk-text);
  padding: 14px 14px 12px;
  outline: none;
}
.bk-input:focus,
.bk-select:focus,
.bk-textarea:focus{
  border-bottom-color: var(--bk-primary);
}
.bk-input.is-invalid{
  border-bottom-color: var(--bk-danger-text);
}
.bk-field-error{
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--bk-danger-text);
}
.bk-textarea{
  min-height: 110px;
  resize: vertical;
}

.bk-summary-card{
  background: var(--bk-panel);
  border-radius: 16px;
  padding: 18px;
  box-shadow: var(--bk-shadow-md);
  align-self:start;
}

.bk-summary-title{
  margin-bottom: 14px;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -.02em;
}

.bk-summary-row{
  display:flex;
  justify-content:space-between;
  gap: 12px;
  padding: 10px 0;
}
.bk-summary-row span{
  color: var(--bk-muted);
  font-size: .875rem;
}
.bk-summary-row strong{
  color: var(--bk-text);
  font-size: .875rem;
  text-align:right;
}

.bk-summary-price-original{
  color: var(--bk-muted);
  text-decoration: line-through;
  font-weight: 600;
  margin-right: 6px;
}
.bk-summary-price-discounted{
  color: var(--bk-primary);
}
.bk-summary-discount-tag{
  margin: 4px 0 0;
  color: var(--bk-primary);
  font-size: .75rem;
  font-weight: 700;
  text-align: right;
}

.bk-summary-deposit-notice{
  margin: 4px 0 0;
  padding-top: 10px;
  border-top: 1px dashed var(--bk-outline-variant);
  color: var(--bk-muted);
  font-size: .75rem;
  line-height: 1.5;
}

/* buttons */
.bk-btn{
  min-height: 46px;
  padding: 12px 18px;
  border-radius: 10px;
  border: 0;
  cursor:pointer;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
  transition: transform .2s ease, opacity .2s ease;
}
.bk-btn:active{
  transform: scale(.985);
}
.bk-btn:disabled{
  opacity: .5;
  cursor:not-allowed;
}

.bk-btn-primary{
  background: var(--bk-primary);
  color: var(--bk-button-text-color);
}

.bk-btn-secondary,
.bk-btn-ghost{
  background: transparent;
  color: var(--bk-primary);
}
.bk-btn-secondary:hover,
.bk-btn-ghost:hover{
  background: var(--bk-surface-high);
}

/* bottom nav */
.bk-bottom-nav{
  position: sticky;
  bottom: 0;
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap: 12px;
  padding: 16px 24px;
  background: color-mix(in srgb, var(--bk-panel) 82%, white 18%);
  backdrop-filter: blur(20px);
  box-shadow: 0 -4px 16px rgba(42,52,57,.04);
}

.bk-success-screen{
  padding: 24px;
}

.bk-success-card{
  background: var(--bk-panel);
  border-radius: 18px;
  padding: 24px;
  box-shadow: var(--bk-shadow-md);
}

.bk-success-actions{
  display:flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap:wrap;
}

.bk-waitlist{
    display:grid;
    gap:12px;
    margin-top:12px;
    padding:14px;
    border:1px solid var(--bk-outline-variant);
    border-radius:12px;
}

.bk-empty-state{
  background: var(--bk-surface-low);
  border-radius: 14px;
  padding: 16px;
  color: var(--bk-muted);
  font-size: .875rem;
  line-height: 1.5;
}

/* processing */
.bk-processing{
  position:absolute;
  inset:0;
  display:grid;
  place-items:center;
  background: rgba(255,255,255,.72);
  border-radius: 12px;
}

.bk-processing-card{
  width: min(420px, calc(100% - 32px));
  background: var(--bk-panel);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--bk-shadow-lg);
}

.bk-processing-title{
  font-weight: 800;
  margin-bottom: 6px;
}

.bk-processing-copy{
  color: var(--bk-muted);
  line-height: 1.5;
  font-size: .875rem;
}

.bk-progress{
  margin-top: 12px;
  height: 8px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--bk-outline-variant) 25%, white 75%);
  overflow:hidden;
}
.bk-progress > div{
  height: 100%;
  width: 65%;
  background: var(--bk-primary);
}

/* select arrow */
select.bk-select{
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #9ca3af 50%),
    linear-gradient(135deg, #9ca3af 50%, transparent 50%);
  background-position:
    calc(100% - 18px) calc(50% - 3px),
    calc(100% - 13px) calc(50% - 3px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  padding-right: 34px;
}

/* responsive */
@media (max-width: 900px){
  .bk-datetime-step,
  .bk-details-layout{
    grid-template-columns: 1fr;
  }

  .bk-datetime-main{
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px){
  .bk-inline{
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    border-radius: 0;
    margin: 0;
    z-index: 2147483647;
  }

  .bk-flow-inline{
    height: 100%;
  }

  .bk-step-topbar{
    padding: 16px;
  }

  .bk-step-body{
    padding: 18px 16px 96px;
  }

  .bk-flow-inline .bk-step-body{
    padding: 16px 16px 20px;
  }

  .bk-form-grid{
    grid-template-columns: 1fr;
  }

  .bk-field-full{
    grid-column: auto;
  }

  .bk-staff-grid{
    grid-template-columns: 1fr;
  }

  .bk-bottom-nav{
    padding: 14px 16px;
  }

  .bk-flow-inline .bk-bottom-nav{
    padding: 12px 16px;
  }

  /* Op mobiel wordt .bk-modal hieronder full-screen geforceerd (100dvh), maar .bk-flow-popup
     zelf was gewoon blok-layout — bij weinig content (bv. maar 2 vestigingen) kwam de
     knoppenbalk dan niet onderaan het scherm te staan (position: sticky doet niets als er
     nooit gescrold hoeft te worden), met een lege grijze strook eronder tot gevolg. Zelfde
     flex-fix als .bk-flow-inline al gebruikt: body vult de resterende ruimte en scrolt zelf,
     de knoppenbalk staat daardoor altijd aan de onderkant, ongeacht hoeveel content er is. */
  .bk-flow-popup{
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .bk-flow-popup .bk-step-body{
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .bk-modal{
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100dvh !important;
    max-width: none !important;
    max-height: none !important;
    border-radius: 0 !important;
    /* geen "transform: none !important" hier — dat zou de open/close-animatie
       hieronder blokkeren (!important op een statische property wint altijd van
       de geanimeerde waarde). Fixed + volledige inset houdt 'm alsnog gevuld. */
    animation: bk-modal-in-mobile .26s cubic-bezier(.22,1,.36,1) both;
  }
  .bk-modal.bk-closing{
    animation: bk-modal-out-mobile .18s cubic-bezier(.4,0,1,1) both;
  }

  .bk-overlay{
    background: rgba(17,24,39,.28);
    backdrop-filter: blur(4px);
  }
}

@keyframes bk-modal-in-mobile{
  from{ opacity: 0; transform: translateY(100%); }
  to{ opacity: 1; transform: translateY(0); }
}
@keyframes bk-modal-out-mobile{
  from{ opacity: 1; transform: translateY(0); }
  to{ opacity: 0; transform: translateY(100%); }
}

@media (prefers-reduced-motion: reduce){
  .bk-modal{ animation: none; }
  .bk-modal.bk-closing{ animation: none; }
}
`;
}